import PropTypes from 'prop-types';
import { alpha, styled } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import { projectInfo, projectSections } from './config';

const Workspace = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '280px minmax(0, 1fr) 280px',
  gap: theme.spacing(3),
  alignItems: 'start',
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: '240px minmax(0, 1fr)',
  },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}));

const SidePanel = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  backgroundColor: theme.palette.background.paper,
}));

const ProjectThumb = styled(Avatar)(({ theme }) => ({
  width: '100%',
  height: 116,
  borderRadius: 12,
  backgroundColor: theme.palette.background.neutral,
}));

const NavButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'activeItem',
})(({ theme, activeItem }) => ({
  justifyContent: 'flex-start',
  width: '100%',
  borderRadius: 10,
  textTransform: 'none',
  padding: theme.spacing(1, 1.2),
  color: activeItem ? theme.palette.primary.main : theme.palette.text.secondary,
  backgroundColor: activeItem ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
  border: activeItem ? `1px solid ${alpha(theme.palette.primary.main, 0.28)}` : '1px solid transparent',
  fontWeight: activeItem ? 700 : 500,
  '&:hover': {
    backgroundColor: activeItem ? alpha(theme.palette.primary.main, 0.12) : theme.palette.action.hover,
  },
}));

export default function WorkspaceLayout({ activeScreen, mainContent, sideContent, onSelectScreen }) {
  return (
    <Workspace>
      <SidePanel sx={{ p: 2, position: { md: 'sticky' }, top: { md: 24 } }}>
        <Stack spacing={2}>
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            sx={{
              justifyContent: 'flex-start',
              px: 0,
              minWidth: 0,
              color: 'text.secondary',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            TEST
          </Button>

          <Box>
            <ProjectThumb variant="rounded" src={projectInfo.cover} alt="project cover" />
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mt: 1.5 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                  {projectInfo.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {projectInfo.type}
                </Typography>
              </Box>
              <IconButton size="small">
                <MoreVertRoundedIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>

          {projectSections.map((section) => (
            <Box key={section.title}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.disabled',
                  fontWeight: 800,
                  letterSpacing: 0.6,
                  px: 1,
                  mb: 1,
                  display: 'block',
                }}
              >
                {section.title}
              </Typography>

              <Stack spacing={0.6}>
                {section.items.map((item) => {
                  const isActive = item.key === activeScreen;
                  return (
                    <NavButton
                      key={item.key}
                      activeItem={isActive ? 1 : 0}
                      onClick={() => onSelectScreen(item.key)}
                      startIcon={
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: isActive ? 'primary.main' : 'text.disabled',
                          }}
                        />
                      }
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                        <span>{item.label}</span>
                        {item.badge ? <Chip label={item.badge} size="small" sx={{ height: 20 }} /> : null}
                      </Box>
                    </NavButton>
                  );
                })}
              </Stack>

              {section.title !== 'DEPLOY' ? <Divider sx={{ my: 1.5 }} /> : null}
            </Box>
          ))}
        </Stack>
      </SidePanel>

      <Box sx={{ minWidth: 0 }}>{mainContent}</Box>

      <Box
        sx={{
          minWidth: 0,
          display: { xs: 'none', lg: 'block' },
          position: { lg: 'sticky' },
          top: { lg: 24 },
        }}
      >
        {sideContent}
      </Box>
    </Workspace>
  );
}

WorkspaceLayout.propTypes = {
  activeScreen: PropTypes.string.isRequired,
  mainContent: PropTypes.node.isRequired,
  sideContent: PropTypes.node,
  onSelectScreen: PropTypes.func.isRequired,
};

WorkspaceLayout.defaultProps = {
  sideContent: null,
};
